// components/document-settings/TemplatesTab.jsx
// Gestione template documenti DOCX

import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { 
  FileText, Upload, Download, Plus, Edit, Trash2, Save, Check, 
  AlertCircle, RefreshCw, FileUp, CheckCircle 
} from 'lucide-react';

const documentTypeLabels = {
  'estimate_customer': 'Preventivo Cliente',
  'estimate_detailed': 'Preventivo Dettagliato',
  'contract_customer': 'Contratto Cliente',
  'sal_report': 'Report SAL',
  'invoice_notice': 'Avviso di Fattura'
};

export const TemplatesTab = ({
  templates,
  loading,
  showTemplateModal,
  setShowTemplateModal,
  editingTemplate,
  setEditingTemplate,
  templateForm,
  setTemplateForm,
  validationResult,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  onUploadDocx,
  onValidateTemplate,
  onDownloadSampleTemplate,
  onSetActiveTemplate
}) => {
  const resetForm = () => {
    setTemplateForm({
      template_name: '',
      template_code: '',
      document_type: 'estimate_customer',
      version: '1.0',
      notes: ''
    });
    setEditingTemplate(null);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      template_name: template.template_name,
      template_code: template.template_code,
      document_type: template.document_type,
      version: template.version,
      notes: template.notes || ''
    });
    setShowTemplateModal(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Template Documenti</h3>
          <p className="text-sm text-gray-500">Gestisci i template DOCX per preventivi e contratti</p>
        </div>
        <div className="flex gap-2">
          <Select onValueChange={onDownloadSampleTemplate}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Scarica esempio..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="estimate_customer">Preventivo Cliente</SelectItem>
              <SelectItem value="estimate_detailed">Preventivo Dettagliato</SelectItem>
              <SelectItem value="contract_customer">Contratto Cliente</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => {
              resetForm();
              setShowTemplateModal(true);
            }} 
            className="bg-[#FF7819] hover:bg-[#E56A10]"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Nuovo Template
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {templates.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Nessun template caricato</p>
              <p className="text-sm">Crea un nuovo template per iniziare</p>
            </CardContent>
          </Card>
        ) : (
          templates.map(template => (
            <Card key={template.id} className={template.is_active ? 'border-green-300 bg-green-50/30' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{template.template_name}</h4>
                      <Badge variant="outline">{template.template_code}</Badge>
                      <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                        {documentTypeLabels[template.document_type]}
                      </Badge>
                      {template.is_active && (
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          <Check className="w-3 h-3 mr-1" />
                          Attivo
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Versione {template.version} • {template.source_docx_path ? 'File caricato' : 'Nessun file'}
                    </p>
                    {template.notes && (
                      <p className="text-sm text-gray-600 mt-2">{template.notes}</p>
                    )}
                    
                    {/* Validation result */}
                    {validationResult?.templateId === template.id && (
                      <div className={`mt-3 p-3 rounded-lg ${validationResult.is_valid ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {validationResult.is_valid ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                          )}
                          <span className="font-medium text-sm">
                            {validationResult.is_valid ? 'Template valido' : 'Placeholder da verificare'}
                          </span>
                        </div>
                        {validationResult.invalid_placeholders.length > 0 && (
                          <div className="text-sm text-amber-700">
                            <p className="font-medium">Placeholder non riconosciuti:</p>
                            <ul className="list-disc list-inside mt-1">
                              {validationResult.invalid_placeholders.map((p, i) => (
                                <li key={`placeholder-${p}`}><code className="bg-amber-100 px-1 rounded">{p}</code></li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {template.source_docx_path && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onValidateTemplate(template.id)}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Valida
                        </Button>
                        {!template.is_active && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600"
                            onClick={() => onSetActiveTemplate(template.id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Attiva
                          </Button>
                        )}
                      </>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".docx"
                        className="hidden"
                        onChange={(e) => onUploadDocx(template.id, e.target.files[0])}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          <FileUp className="w-4 h-4 mr-1" />
                          Carica DOCX
                        </span>
                      </Button>
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(template)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                      onClick={() => onDeleteTemplate(template.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Modifica Template' : 'Nuovo Template'}</DialogTitle>
            <DialogDescription>
              Configura i dettagli del template documento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome Template</Label>
              <Input
                value={templateForm.template_name}
                onChange={e => setTemplateForm({...templateForm, template_name: e.target.value})}
                placeholder="es. Preventivo Standard v2"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Codice</Label>
                <Input
                  value={templateForm.template_code}
                  onChange={e => setTemplateForm({...templateForm, template_code: e.target.value})}
                  placeholder="es. PREV-STD-V2"
                />
              </div>
              <div>
                <Label>Versione</Label>
                <Input
                  value={templateForm.version}
                  onChange={e => setTemplateForm({...templateForm, version: e.target.value})}
                  placeholder="1.0"
                />
              </div>
            </div>
            <div>
              <Label>Tipo Documento</Label>
              <Select 
                value={templateForm.document_type}
                onValueChange={v => setTemplateForm({...templateForm, document_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(documentTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={templateForm.notes}
                onChange={e => setTemplateForm({...templateForm, notes: e.target.value})}
                placeholder="Descrizione o note sul template..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={editingTemplate ? onUpdateTemplate : onCreateTemplate}
              disabled={loading}
              className="bg-[#FF7819] hover:bg-[#E56A10]"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {editingTemplate ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesTab;
